import { commonLanguageFiles } from "client/config";
import { url } from "client/config/url";
import { LanguageProvider } from "client/contexts/LanguageProvider";
import { translateFunc } from "client/utils/translateFunc";
import { MemeGeneratorContent } from "components/modules/Content/MemeGeneratorContent/MemeGeneratorContent";
import { MemeGeneratorProps } from "components/modules/Editors/MemeGenerator/MemeGenerator";
import { Footer } from "components/modules/Layout/Footer/Footer";
import { Tools } from "components/modules/Layout/Tools/Tools";
import { MemeGeneratorTutorial } from "components/modules/Tutorial/MemeGeneratorTutorial/MemeGeneratorTutorial";
import { fetchMemes } from "lib/memeGenerator/fetchMemes";
import { fetchSingleMeme } from "lib/memeGenerator/fetchSingleMeme";
import type { GetServerSideProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import dynamic from "next/dynamic";
import Head from "next/head";
import translatedRoutedSlugs from "translatedRouteSlugs";
import type { MemeTemplate } from "types/meme";

interface PageProps {
    defaultMeme: MemeTemplate;
    memes: MemeTemplate[];
    slug: string;
}

const MemeGenerator = dynamic<MemeGeneratorProps>(() =>
    import("components/modules/Editors/MemeGenerator/MemeGenerator").then((mod) => mod.MemeGenerator),
);

const Page = ({ memes, defaultMeme, slug }: PageProps) => {
    const title = translateFunc("edit-page-title", { title: defaultMeme.title });
    const description = translateFunc("edit-page-meta-description", { title: defaultMeme.title });
    const defaultPageSlug = translatedRoutedSlugs["/meme-generator/edit/:slug"]["en"].replace(":slug", slug);

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta name="description" content={description} />
                <meta property="og:url" content={`${url}/${defaultPageSlug}`} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:image" content="/social.png" />
                <meta property="twitter:card" content="summary" />
                <meta property="twitter:title" content={title} />
                <meta property="twitter:description" content={description} />
            </Head>
            <div className="max-w-1440 page-shadow dark:bg-darkSurface-100">
                <MemeGenerator initialMemes={memes} defaultMeme={defaultMeme} isEditPage={true} />
                <div className="pb-12 max-w-1440 border-x md:pb-0">
                    <MemeGeneratorContent />
                    <MemeGeneratorTutorial />
                    <Tools />
                    <Footer url="/meme-generator/edit/:slug" urlSlugVariables={{ slug }} />
                </div>
            </div>
        </>
    );
};

const LanguageWrapper = (props: PageProps) => (
    <LanguageProvider files={files}>
        <Page {...props} />
    </LanguageProvider>
);

export default LanguageWrapper;

const files = ["meme-generator", "uploader", ...commonLanguageFiles];

export const getServerSideProps: GetServerSideProps<{ memes: MemeTemplate[]; defaultMeme: MemeTemplate }> = async ({
    query,
    locale,
}) => {
    const slug = Array.isArray(query["slug"]) ? query["slug"][0] : query["slug"] || "";
    const { meme } = await fetchSingleMeme({ slug });
    if (!meme) return { notFound: true };
    const { memes, error } = await getMemes(meme);
    if (!memes || error) throw new Error(`${error}`);
    meme.slugKey = meme.slugKey.toLowerCase();
    return {
        props: {
            defaultMeme: meme,
            memes,
            slug,
            ...(await serverSideTranslations(locale || "", files)),
        },
    };
};

async function getMemes(meme: MemeTemplate) {
    const totalMemes = 18;
    const minId = Math.max(+meme.id - totalMemes / 2, 0);
    const ids: string[] = [];
    for (let i = 0; i < totalMemes; i++) {
        ids.push(`${minId + i}`);
    }
    const { memes, error } = await fetchMemes({ q: "", page: 1, filter_by: `id:[${ids.join(",")}]` });
    if (memes && memes.length < totalMemes) {
        const remaining = totalMemes - memes.length;
        const { memes: newMemes, error } = await fetchMemes({ q: "", page: 1, per_page: remaining });
        newMemes && !error && memes.push(...newMemes);
    }
    return { memes, error };
}
